import {
  type ReactNode,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  generate,
  gradeExercise,
  type AudioPlan,
  type Exercise,
  type ExerciseAnswer,
  type GradingResult,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import {
  ExerciseCard,
  StagePips,
  FeedbackBox,
  AudioStatusIndicator,
} from "@music-app/ui-components";
import {
  buildAllItems,
  buildPlacementSession,
  seedCardsFromPlacement,
  placementEligibleNodes,
  guitarPlacementNodes,
  MVP_NODES,
  type CurriculumNode,
  type PlacementResult,
} from "@music-app/learning-system";
import {
  useCardsStore,
  usePrefsStore,
  saveCards,
} from "@music-app/persistence";
import { filterByPrefs } from "./session-prefs.js";
import { getRenderer } from "../lib/exercise-renderers/registry.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";

type AudioStatus = "loading" | "ready" | "synth-fallback" | "error";
type Phase = "intro" | "running" | "done";
type ItemPhase = "playing" | "awaiting" | "graded";

const CADENCE_PIP_COUNT = 4;
const FAST_MS = 3000;
const SLOW_MS = 8000;

export type PlacementVariant = "ear-training" | "guitar";

interface VariantCopy {
  eyebrow: string;
  title: string;
  introSubhead: (count: number) => string;
  /** Where "Start practicing" / "Skip" land after the session. */
  doneRoute: string;
  /** Label under the intro's "retake later" bullet. */
  resetHint: string;
}

const VARIANT_COPY: Record<PlacementVariant, VariantCopy> = {
  "ear-training": {
    eyebrow: "diagnostic",
    title: "Placement",
    introSubhead: (n) =>
      `About ${n} questions, ~2 minutes. We use your answers (and how fast you give them) to skip ahead in things you already know — so the scheduler spends its time where you actually need it.`,
    doneRoute: "/review",
    resetHint: "Settings → Reset placement",
  },
  guitar: {
    eyebrow: "guitar diagnostic",
    title: "Guitar Placement",
    introSubhead: (n) =>
      `About ${n} questions on the neck, ~2 minutes. Answer on the fretboard — we use the shapes you already know (CAGED, diatonic chords, scale degrees) to start you at the right rung instead of grinding up from the bottom.`,
    doneRoute: "/guitar",
    resetHint: "the Guitar Hub, or Settings → Reset guitar placement",
  },
};

/**
 * Placement test — diagnostic that pre-populates the FSRS card store so an
 * intermediate user doesn't grind through items they already own. Pure
 * scoring lives in `@music-app/learning-system/placement`; this page wires
 * it to the audio engine and the cards store.
 *
 * Two variants share this flow: the ear-training placement (`/placement`)
 * and the guitar placement (`/guitar/placement`). They differ only in the
 * node pool, completion flag / seed, done-redirect, and copy — the answer
 * rendering (multiple-choice, solfège, fretboard) is identical, dispatched
 * through the shared `getRenderer` registry.
 *
 * Flow: intro → running (one item at a time, capture rating + responseMs) →
 *       done (summary + "Start practicing" CTA).
 *
 * Distinct from Practice: no FSRS writes during the session, no session
 * record persisted, no per-answer chime. Cards are written once at the end,
 * after the user clicks "Start practicing".
 */
export function PlacementPage({
  variant = "ear-training",
}: { variant?: PlacementVariant } = {}): ReactNode {
  const enabledInstrumentTracks = usePrefsStore((s) => s.enabledInstrumentTracks);
  // Guitar placement is only reachable when the track is enabled.
  if (variant === "guitar" && !enabledInstrumentTracks.includes("guitar")) {
    return <Navigate to="/settings" replace />;
  }
  return <PlacementContent variant={variant} />;
}

function PlacementContent({ variant }: { variant: PlacementVariant }): ReactNode {
  const navigate = useNavigate();
  const isGuitar = variant === "guitar";
  const copy = VARIANT_COPY[variant];

  const cardsHydrate = useCardsStore((s) => s.hydrate);
  const setPlacementCompleted = usePrefsStore((s) => s.setPlacementCompleted);
  const setGuitarPlacementCompleted = usePrefsStore((s) => s.setGuitarPlacementCompleted);
  const includeLeadingTone = usePrefsStore((s) => s.includeLeadingTone);
  const instrument = usePrefsStore((s) => s.instrument);
  const placementSeed = usePrefsStore((s) => s.placementSeed);
  const guitarPlacementSeed = usePrefsStore((s) => s.guitarPlacementSeed);

  const seed = isGuitar ? guitarPlacementSeed : placementSeed;
  const setCompleted = isGuitar ? setGuitarPlacementCompleted : setPlacementCompleted;

  /** Nodes eligible for this variant's pool. Guitar samples the guitar
   *  curriculum; ear-training excludes guitar + dictation. */
  const poolNodes = useCallback(
    (): CurriculumNode[] =>
      isGuitar ? guitarPlacementNodes(MVP_NODES) : placementEligibleNodes(MVP_NODES),
    [isGuitar],
  );

  /** Items the user will work through. Built once on mount so the queue
   *  doesn't reshuffle if a pref toggles mid-session. */
  const sessionItems = useMemo(() => {
    const eligibleNodes = poolNodes();
    const eligibleIds = new Set(eligibleNodes.map((n) => n.id));
    const filtered = filterByPrefs(buildAllItems(), { includeLeadingTone }).filter((i) =>
      eligibleIds.has(i.nodeId),
    );
    return buildPlacementSession(eligibleNodes, filtered, { seed });
  }, [includeLeadingTone, seed, poolNodes]);

  const [phase, setPhase] = useState<Phase>("intro");
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState<PlacementResult[]>([]);

  const [audioStatus, setAudioStatus] = useState<AudioStatus>("loading");
  const [pianoProgress, setPianoProgress] = useState<number | undefined>(undefined);
  const [audioInitiating, setAudioInitiating] = useState(false);

  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [itemPhase, setItemPhase] = useState<ItemPhase>("playing");
  const [pendingAnswer, setPendingAnswer] = useState<ExerciseAnswer | null>(null);
  const [gradingResult, setGradingResult] = useState<GradingResult | null>(null);
  const awaitingStartRef = useRef<number>(0);

  const [activePip, setActivePip] = useState<number | null>(null);
  const [donePips, setDonePips] = useState<number[]>([]);
  const bassIndexRef = useRef<number>(0);

  const playerRef = useRef<AudioPlayer | null>(null);
  const ensureAudioPromiseRef = useRef<Promise<AudioPlayer> | null>(null);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, []);

  const ensureAudio = (): Promise<AudioPlayer> => {
    if (playerRef.current) return Promise.resolve(playerRef.current);
    if (ensureAudioPromiseRef.current) return ensureAudioPromiseRef.current;

    const promise = (async () => {
      setAudioInitiating(true);
      try {
        const player = createAudioPlayer();
        await player.init();
        playerRef.current = player;

        if (instrument === "synth") {
          setAudioStatus("ready");
          setPianoProgress(undefined);
          return player;
        }

        setAudioStatus("loading");
        setPianoProgress(0);
        try {
          await player.loadInstrument(instrument, {
            onProgress: (loaded, total) => {
              setPianoProgress(total > 0 ? loaded / total : 0);
            },
          });
          try {
            player.setInstrument(instrument);
            setAudioStatus("ready");
          } catch {
            setAudioStatus("synth-fallback");
          }
        } catch {
          setAudioStatus("synth-fallback");
        } finally {
          setPianoProgress(undefined);
        }
        return player;
      } finally {
        setAudioInitiating(false);
      }
    })();

    ensureAudioPromiseRef.current = promise;
    promise.finally(() => {
      ensureAudioPromiseRef.current = null;
    });
    return promise;
  };

  const handleNoteStart = useCallback((event: { midi: number; voice?: string }) => {
    if (event.voice === "bass") {
      const i = bassIndexRef.current;
      bassIndexRef.current = i + 1;
      setActivePip(i);
    }
  }, []);

  const handleNoteEnd = useCallback((event: { midi: number; voice?: string }) => {
    if (event.voice === "bass") {
      setActivePip(null);
      setDonePips((d) => [...d, d.length]);
    }
  }, []);

  const playAudio = async (player: AudioPlayer, audio: AudioPlan) => {
    setDonePips([]);
    setActivePip(null);
    bassIndexRef.current = 0;
    await player.play(audio, {
      onNoteStart: handleNoteStart,
      onNoteEnd: handleNoteEnd,
    });
    setActivePip(null);
  };

  const playItemAt = async (player: AudioPlayer, itemIndex: number) => {
    const item = sessionItems[itemIndex];
    if (!item) return;
    const ex = generate(item, Date.now());
    setExercise(ex);
    setPendingAnswer(null);
    setGradingResult(null);
    setItemPhase("playing");
    await playAudio(player, ex.audio);
    awaitingStartRef.current = performance.now();
    setItemPhase("awaiting");
  };

  const handleStart = async () => {
    if (sessionItems.length === 0) {
      // No items in the curriculum — finish immediately. Edge case; useful
      // mostly for tests with empty fixtures.
      setPhase("done");
      return;
    }
    const player = await ensureAudio();
    setPhase("running");
    setIdx(0);
    setResults([]);
    await playItemAt(player, 0);
  };

  const replayAudio = async () => {
    if (!exercise || !playerRef.current) return;
    if (itemPhase !== "awaiting" && itemPhase !== "graded") return;
    setItemPhase("playing");
    await playAudio(playerRef.current, exercise.audio);
    awaitingStartRef.current = performance.now();
    // After replay, return to whichever resting phase we were in.
    setItemPhase((prev) => (prev === "playing" ? "awaiting" : prev));
  };

  const recordAndAdvance = (result: PlacementResult) => {
    const next = [...results, result];
    setResults(next);
    const nextIdx = idx + 1;
    if (nextIdx >= sessionItems.length) {
      setPhase("done");
      setExercise(null);
      return;
    }
    setIdx(nextIdx);
    if (playerRef.current) void playItemAt(playerRef.current, nextIdx);
  };

  const classifyRating = (correct: boolean, responseMs: number): PlacementResult["rating"] => {
    if (!correct) return "wrong";
    if (responseMs < FAST_MS) return "fast";
    if (responseMs > SLOW_MS) return "slow";
    return "ok";
  };

  const handleSubmit = (answer: ExerciseAnswer) => {
    if (!exercise || itemPhase !== "awaiting") return;
    const result = gradeExercise(exercise, answer);
    const responseMs = performance.now() - awaitingStartRef.current;
    const rating = classifyRating(result.correct, responseMs);
    setPendingAnswer(answer);
    setGradingResult(result);
    setItemPhase("graded");
    // Brief pause so the user can register correctness, then advance.
    window.setTimeout(() => {
      recordAndAdvance({ itemId: exercise.itemId, rating, responseMs });
    }, 800);
  };

  const handleSkip = () => {
    if (!exercise) return;
    if (itemPhase !== "awaiting" && itemPhase !== "playing") return;
    recordAndAdvance({ itemId: exercise.itemId, rating: "skipped", responseMs: 0 });
  };

  const handleSeedAndStart = async () => {
    setSeeding(true);
    try {
      const eligibleNodes = poolNodes();
      const eligibleIds = new Set(eligibleNodes.map((n) => n.id));
      const cards = seedCardsFromPlacement(
        results,
        buildAllItems().filter((i) => eligibleIds.has(i.nodeId)),
        eligibleNodes,
      );
      await saveCards(cards);
      await cardsHydrate();
      await setCompleted(true);
      navigate(copy.doneRoute);
    } finally {
      setSeeding(false);
    }
  };

  const handleSkipPlacement = async () => {
    // Mark complete without seeding — the user opts to start from scratch.
    await setCompleted(true);
    navigate(copy.doneRoute);
  };

  /* ─── render ────────────────────────────────────────────────────── */

  if (phase === "intro") {
    return (
      <div className="max-w-2xl mx-auto py-9 px-8">
        <PageHeader
          eyebrow={copy.eyebrow}
          title={copy.title}
          subhead={copy.introSubhead(sessionItems.length)}
        />
        <div className="border border-ink/10 bg-paperWarm p-5 mb-8">
          <ul className="space-y-2 text-inkSoft text-sm">
            <li className="font-serif italic">
              <strong className="text-ink not-italic font-medium">Wrong answers don&apos;t punish.</strong>{" "}
              They tell us where to start fresh.
            </li>
            <li className="font-serif italic">
              <strong className="text-ink not-italic font-medium">Skip anything you don&apos;t recognize.</strong>{" "}
              No penalty.
            </li>
            <li className="font-serif italic">
              <strong className="text-ink not-italic font-medium">You can retake it later</strong>{" "}
              from {copy.resetHint}.
            </li>
          </ul>
        </div>
        <div className="flex gap-3 flex-wrap items-start">
          <button
            type="button"
            onClick={() => void handleStart()}
            disabled={audioInitiating || sessionItems.length === 0}
            className={buttonClasses("primary")}
          >
            {audioInitiating
              ? pianoProgress !== undefined
                ? `Loading samples… ${Math.round(pianoProgress * 100)}%`
                : "Loading samples…"
              : "▶ Begin →"}
          </button>
          <button
            type="button"
            onClick={() => void handleSkipPlacement()}
            className={buttonClasses("secondary")}
          >
            Skip — start from scratch
          </button>
        </div>
      </div>
    );
  }

  if (phase === "done") {
    const seededReview = results.filter((r) => r.rating === "fast" || r.rating === "ok").length;
    const seededLearning = results.filter((r) => r.rating === "slow").length;
    const left = results.filter((r) => r.rating === "wrong" || r.rating === "skipped").length;
    return (
      <div className="max-w-2xl mx-auto py-9 px-8">
        <PageHeader
          eyebrow="placement complete"
          title="You're in"
          emphasis="in"
          subhead="Based on what you answered, here's where the scheduler will start you. You can adjust focus anytime from Practice."
        />
        <div className="border border-ink/10 p-5 mb-8">
          <ul className="space-y-3" aria-label="Placement summary">
            <li className="flex items-baseline gap-3">
              <span className="font-mono text-2xl font-medium text-success tabular-nums">{seededReview}</span>
              <span className="font-serif italic text-inkSoft text-sm">items placed at intermediate stability</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="font-mono text-2xl font-medium text-info tabular-nums">{seededLearning}</span>
              <span className="font-serif italic text-inkSoft text-sm">items placed in early learning</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="font-mono text-2xl font-medium text-ink tabular-nums">{left}</span>
              <span className="font-serif italic text-inkSoft text-sm">items left as new — those will start fresh</span>
            </li>
          </ul>
        </div>
        <button
          type="button"
          onClick={() => void handleSeedAndStart()}
          disabled={seeding}
          className={buttonClasses("primary")}
        >
          {seeding ? "Starting…" : "▶ Start practicing →"}
        </button>
      </div>
    );
  }

  // phase === "running"
  const currentItemNumber = idx + 1;
  const totalItems = sessionItems.length;
  const isPlaying = itemPhase === "playing";
  const isAwaiting = itemPhase === "awaiting";
  const isGraded = itemPhase === "graded";
  const correctChosen = isGraded && gradingResult?.correct === true;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      {/* progress rule — 2px track, accent fill proportional to position */}
      <div className="h-0.5 bg-ink/10 mb-6" role="progressbar" aria-valuenow={currentItemNumber} aria-valuemax={totalItems}>
        <div
          className="h-full bg-accent transition-all duration-300"
          style={{ width: `${(currentItemNumber / totalItems) * 100}%` }}
        />
      </div>
      <PageHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subhead="Answers don't punish you, they place you."
        right={
          <AudioStatusIndicator
            status={audioStatus}
            progress={pianoProgress}
            instrumentLabel={
              audioStatus === "ready"
                ? instrument
                : audioStatus === "synth-fallback"
                  ? "synth"
                  : undefined
            }
          />
        }
      />

      <ExerciseCard
        context={
          exercise ? (
            <>
              Key:{" "}
              <strong className="font-serif italic text-base text-ink">
                {exercise.meta.keyName ?? "—"}
              </strong>
            </>
          ) : (
            "—"
          )
        }
        status={`${currentItemNumber} of ${totalItems}`}
        prompt={exercise?.prompt ?? "Listen…"}
        stagePips={
          isPlaying ? (
            <StagePips count={CADENCE_PIP_COUNT} active={activePip} done={donePips} />
          ) : undefined
        }
        controls={
          <div className="flex gap-2 flex-wrap">
            {(isAwaiting || isGraded) && (
              <button
                type="button"
                onClick={() => void replayAudio()}
                className={buttonClasses("secondary")}
              >
                ↻ Replay
              </button>
            )}
            {(isAwaiting || isPlaying) && (
              <button
                type="button"
                onClick={handleSkip}
                disabled={isPlaying}
                className={buttonClasses("secondary")}
              >
                Skip
              </button>
            )}
          </div>
        }
        options={
          exercise
            ? getRenderer(exercise.exerciseType)({
                exercise,
                pendingAnswer,
                gradingResult,
                disabled: itemPhase !== "awaiting",
                onAnswerChange: setPendingAnswer,
                onSubmit: handleSubmit,
              })
            : null
        }
        feedback={
          <FeedbackBox
            state={isGraded ? (correctChosen ? "correct" : "wrong") : "pending"}
          >
            {isGraded && exercise
              ? correctChosen
                ? "Got it."
                : `It was ${exercise.options.find((o) => o.id === exercise.correctAnswerId)?.label ?? "—"}.`
              : isPlaying
                ? "Listen carefully."
                : isAwaiting
                  ? "Pick the answer that matches what you heard, or skip."
                  : ""}
          </FeedbackBox>
        }
        footer={
          <div className="flex justify-between text-xs text-inkFaded font-mono">
            <span>diagnostic — not graded</span>
            <span>{exercise?.exerciseType ?? ""}</span>
          </div>
        }
      />
    </div>
  );
}
